import { useCallback, useEffect, useState } from 'react';
import { fetchUnbookedSentReports, setReportBillingExempt } from '../services/unbookedReportsService';
import type { UnbookedSentReportRow } from '../services/unbookedReportsLogic';

/**
 * Laedt die offenen Faelle „versendeter Bericht ohne Leistungsbuchung"
 * (Sicht `v_unbooked_sent_reports`, MIGRATIONS.md Nr. 45) und bietet den
 * einen Ausweg an, den es hier zu bedienen gibt: „nicht abrechenbar".
 *
 * Der zweite Ausweg — buchen — braucht keinen Code: er passiert im Projekt,
 * und die Zeile verschwindet beim naechsten Laden von allein, weil die Sicht
 * das Protokoll auswertet und keinen zweiten Merker fuehrt.
 *
 * EIN Hook fuer beide Anzeigeorte (Meldungsliste der Verwaltung und
 * Integritaetswaechter), damit dort nicht zwei Ladepfade mit zwei
 * Fehlerverhalten entstehen. Die REGELN liegen in `unbookedReportsLogic.ts` und sind ohne
 * React pruefbar; hier steht nur das Laden.
 *
 * Geladen wird IMMER alles, was der Nutzer sehen darf (die Sicht trennt
 * selbst, siehe fetchUnbookedSentReports). Wer nur einen Ausschnitt anzeigen
 * will, filtert `rows` — nicht die Abfrage.
 */
export const useUnbookedSentReports = (enabled: boolean = true) => {
    const [rows, setRows] = useState<UnbookedSentReportRow[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [busyId, setBusyId] = useState<string | null>(null);

    const reload = useCallback(async () => {
        if (!enabled) { setRows([]); return; }
        setIsLoading(true);
        try {
            setRows(await fetchUnbookedSentReports());
        } finally {
            setIsLoading(false);
        }
    }, [enabled]);

    useEffect(() => { void reload(); }, [reload]);

    /**
     * Ausnahme setzen. Erst NACH erfolgreicher RPC aus der Liste nehmen — ein
     * optimistisches Ausblenden haette dem Nutzer bei einem Fehler genau das
     * vorgespielt, was dieser ganze Vorgang abschaffen soll: erledigt
     * aussehen, ohne erledigt zu sein.
     */
    const markExempt = useCallback(async (reportHistoryId: string, reason: string) => {
        setBusyId(reportHistoryId);
        try {
            await setReportBillingExempt(reportHistoryId, true, reason);
            setRows(prev => prev.filter(r => r.report_history_id !== reportHistoryId));
        } finally {
            setBusyId(null);
        }
    }, []);

    return { rows, isLoading, busyId, reload, markExempt };
};
